import type { TranslationModule } from '../types';

export const legislativeChangesTranslations: TranslationModule = {
  ro: {
    seo: {
      title: "Modificări Legislative la Zi din Monitorul Oficial & Alerte Legi | Legea Aplicată",
      description: "Monitorizare oficială zilnică a modificărilor din cele 40 de acte normative (Codul Civil, Penal, Fiscal, Muncii etc.). Text modificat verbatim, intrări în vigoare și alerte automate pe email.",
      keywords: "modificari legislative, monitorul oficial, alerte legi, codul fiscal modificat, codul penal modificat, noutati legislative romania, legislatie la zi, portal legislativ",
      ogTitle: "Modificări Legislative la Zi & Alerte Monitorul Oficial — Legea Aplicată",
      ogDescription: "Toate modificările, completările și abrogările din marile coduri ale României. Alerte automate pe email pentru juriști și profesioniști.",
      ogImageAlt: "Legea Aplicată — Monitorizare Modificări Legislative la Zi din Monitorul Oficial",
      legislationName: "Modificări Legislative Monitorizate — 40 de Acte Normative",
      legislationDesc: "Sistem continuu de monitorizare a modificărilor, completărilor și abrogărilor legislative din România.",
    },
    hero: {
      titlePre: "Modificări Legislative la Zi din ",
      titleHighlight: "Monitorul Oficial",
      subtitle: "Urmărește în timp real modificările, completările și abrogările din cele 40 de coduri și legi fundamentale ale României. Primiți alerte automate pe email la ora 12:00 în ziua apariției noutăților.",
      alertCtaTitle: "Vrei alerte pe email când se modifică o lege din materia ta?",
      alertCtaDesc: "Selectezi materiile dorite (Penal, Civil, Fiscal, Muncă, Administrativ etc.) și primești pe email (funcție Pro) sinteza și textul verbatim integral al modificărilor.",
      alertCtaButton: "Activează Alerte Email (Pro)",
    },
    categories: {
      ariaLabel: "Categoria actului",
      all: "Toate categoriile",
      legislation: "Modificări legislative",
      ril: "RIL — ÎCCJ",
      hg: "Hotărâri de Guvern",
    },
    search: {
      placeholder: "Caută după act (ex: Codul Fiscal, OUG 32, Legea 57, penal)...",
      clear: "Șterge",
      counter: "Afișate: {filtered} din {total} modificări recente",
    },
    domains: {
      all: "Toate materiile (40 acte)",
      penal: "⚖️ Drept Penal & Proc. Penală",
      civil: "📜 Drept Civil & Proc. Civilă",
      fiscal: "💼 Drept Fiscal & Proc. Fiscală",
      munca: "👥 Dreptul Muncii & Securitate",
      administrativ: "🏛️ Drept Administrativ",
      comercial: "🏢 Societăți & Comerț",
    },
    archive: {
      title: "Arhiva permanentă a modificărilor legislative",
      subtitle: "De la cele mai noi la cele mai vechi, după data publicării. Modificările importate rămân disponibile în arhivă.",
      error: "Arhiva nu poate fi încărcată momentan.",
      retry: "Reîncearcă",
      loading: "Se încarcă noutățile legislative...",
      emptyTitle: "Nicio modificare găsită",
      emptyDesc: "Încearcă să schimbi termenul de căutare sau selectează „Toate materiile”.",
      resetFilters: "Resetează filtrele",
      loadMore: "Încarcă modificări mai vechi ({count} rămase)",
    },
    card: {
      publishedMo: "Publicat în M.Of.:",
      effectiveDate: "Intrare în vigoare:",
      articles: "Articole:",
      moreArticles: "+{count} altele",
      detailsLink: "Detalii modificare →",
      consolidatedText: "Text consolidat",
      notifyMe: "Notifică-mă",
      consolidatedTitle: "Consultă articolele consolidate în Legea Aplicată",
      notifyTitle: "Configurează notificări email pentru acest act",
      openConsolidatedArticle: "Deschide Articolul {art} consolidat în Legea Aplicată",
      relatedArticleTitle: "Articol conex încadrat AI în {ref}",
    },
    types: {
      completare: "COMPLETARE",
      modificare: "MODIFICARE",
      abrogare: "ABROGARE",
      suspendare: "SUSPENDARE",
      prorogare: "PROROGARE",
      derogare: "DEROGARE",
      interpretare: "INTERPRETARE",
    },
    specialSection: {
      ariaLabel: "RIL și intervenții speciale API",
      tablistAria: "Tipuri de acte și intervenții normative",
      badge: "Jurisprudență Obligatorie & Acte Conexe",
      title: "Decizii ÎCCJ (RIL / HP), Hotărâri de Guvern & Derogări Legislative",
      description: "Pe lângă modificările directe ale articolelor din coduri, aici găsești deciziile obligatorii ale Înaltei Curți de Casație și Justiție (Recurs în Interesul Legii — RIL și Hotărâri Prealabile — HP), Hotărârile de Guvern (HG) cu norme metodologice de aplicare, Ordonanțele de Urgență (OUG / OG), precum și derogările, suspendările și termenele prorogate care afectează aplicarea articolelor chiar și atunci când textul lor din cod rămâne neschimbat.",
      tabRil: "⚖️ Decizii ÎCCJ (RIL & HP)",
      tabHg: "🏛️ Hotărâri de Guvern & Norme (HG)",
      tabEffects: "🔗 Derogări, Suspendări & Termene Prorogate",
      tabOug: "⚡ Ordonanțe de Urgență & Ordonanțe (OUG / OG)",
      badgeRil: "RIL / Decizie ÎCCJ",
      badgeHg: "Hotărâre de Guvern (HG)",
      badgeOug: "OUG / OG",
      badgeEffects: "Derogare / Normă de Aplicare",
      published: "Publicat:",
      targetArticles: "Articole vizate:",
      viewAnalysis: "Vezi analiza & textul oficial →",
      consolidatedCode: "Cod consolidat",
    },
    howItWorks: {
      title: "Cum funcționează sistemul de monitorizare a modificărilor legislative pe LegeaAplicată",
      step1Title: "1. Crawl Oficial Zilnic la 05:20",
      step1Desc: "În fiecare dimineață la 05:20 (Europe/Bucharest), infrastructura noastră sincronizează automat modificările oficiale pentru a prelua noile relații normative induse sau suferite de cele 40 de legi fundamentale.",
      step2Title: "2. Text Verbatim & Reconciliere",
      step2Desc: "Nu oferim doar o simplă notificare a titlului de lege, ci extragem textul modificat integral verbatim, articolele afectate și un rezumat oficial pregătit special pentru claritate juridică imediată.",
      step3Title: "3. Alerte Email la Ora 12:00",
      step3Desc: "Abonații Pro care și-au confirmat acordul de email primesc zilnic la ora 12:00 (în zilele cu noutăți) buletinul legislativ complet stilizat HTML, strict pentru materiile lor de interes și începând de la data activării opțiunii.",
    },
    faq: {
      title: "Întrebări Frecvente despre Modificările Legislative",
      bottomTextPre: "Descoperă toate cele 45 de instrumente juridice în ",
      bottomGuide: "Ghidul Funcțiilor (45 instrumente)",
      bottomTextMid: " sau consultă ",
      bottomCases: "Dosarele Tale Urmărite",
      bottomTextPost: ".",
      q1: "Cum sunt monitorizate și preluate modificările legislative?",
      a1: "Platforma rulează un proces automatizat zilnic la ora 05:20 (Europe/Bucharest) care sincronizează modificările direct din legislația oficială. Toate modificările, completările și abrogările din cele 40 de acte normative fundamentale sunt reconciliate și încărcate verbatim.",
      q2: "La ce oră se trimit alertele pe email și ce conțin?",
      a2: "Serverul trimite alertele către utilizatorii abonați la ora 12:00 în ziua în care există noutăți în materiile bifate. Emailul include actul modificat, actul modificator, numărul din Monitorul Oficial, data intrării în vigoare, articolele afectate, rezumatul oficial și textul modificat integral verbatim.",
      q3: "Alertele retroactive: primesc modificări din trecut?",
      a3: "Nu. Conform regulii stricte de subscriere („începând de la data bifării”), un utilizator primește exclusiv modificările apărute după momentul exact în care a bifat materia de interes și a confirmat primirea alertelor pe email.",
      q4: "Cum pot dezactiva alertele pe email?",
      a4: "Dezactivarea este instantanee. Poți dezbifa opțiunea de email oricând din pagina Dosare Urmărite (tab-ul Modificări Legislative de Interes) sau din Contul Meu la secțiunea Setări & Alerte Legislative.",
      q5: "Care sunt cele 40 de acte normative monitorizate?",
      a5: "Monitorizarea include marile coduri ale României (Codul Civil, Codul Penal, Codul de Procedură Civilă, Codul de Procedură Penală, Codul Fiscal, Codul de Procedură Fiscală, Codul Muncii, Codul Administrativ, Codul Silvic, Codul Aerian etc.) precum și legile organice fundamentale: Legea societăților 31/1990, Legea insolvenței 85/2014, Legea cadastrului 7/1996, Legea dialogului social 367/2022, Legea contenciosului administrativ 554/2004 etc.",
    },
    detail: {
      ariaLabel: "Modificarea selectată",
      backLink: "← Înapoi la toate modificările",
      errorNotFound: "Modificarea nu a fost găsită în arhivă.",
      errorGeneric: "Detaliile nu pot fi încărcate momentan.",
      retry: "Reîncearcă",
      loading: "Se încarcă modificarea…",
      effectiveDate: "Intrare în vigoare:",
      consolidatedTitle: "Articole modificate în forma consolidată",
      consolidatedSubtitle: "Consultă textul consolidat oficial direct în Legea Aplicată, fără redirecționări externe.",
      openConsolidatedSingle: "Deschide Art. {art} consolidat",
      openConsolidatedAll: "Deschide toate cele {count} articole consolidate",
      openSpecificArticle: "Sau deschide punctual un articol:",
      consolidatedNoticeTitle: "Forma consolidată în Legea Aplicată",
      consolidatedNoticeSubtitle: "Sursa oficială nu a indicat articole punctuale în metadate (intervenție generală sau derogare).",
      openTarget: "Deschide {target}",
      verbatimHeader: "Conținutul modificărilor legislative (text oficial)",
      noModifiedText: "Sursa oficială nu furnizează un text modificat separat pentru această înregistrare. Consultă forma consolidată verificată mai sus.",
      viewArticleConsolidated: "Vezi Art. {art} consolidat →",
      viewArticleInContext: "Vezi Art. {art} în context consolidat →",
      viewLinkConsolidated: "Vezi {num}{ref} consolidat →",
      viewLinkInContext: "Vezi {num}{ref} în context consolidat →",
      fullActSummary: "Textul integral al actului oficial",
    },
    consolidation: {
      noticeAria: "Actualizarea textului legislativ",
      notice: "Formă consolidată din {date}, verificată în aplicație la {verifiedAt}.",
      pending: "O nouă verificare automată este în curs. Textul de mai sus păstrează ultima versiune confirmată.",
      fullLink: "Consultă forma consolidată integrală în aplicație →",
      prevVersion: "Versiunea anterioară din aplicație",
      verifiedText: "Textul consolidat verificat",
      newArticle: "Articol nou în baza locală.",
      initialRecon: "Recuperare inițială: comparație cu versiunea păstrată anterior în aplicație. Poate include mai multe modificări legislative.",
    },
    consolidationChanges: {
      title: "Comparația formelor consolidate · {count} articole",
      subtitle: "Diferențe față de ultima versiune locală verificată. Pot include efectele mai multor acte modificatoare și nu sunt atribuite exclusiv înregistrării API selectate.",
      openArticle: "Deschide articolul consolidat →",
      fullView: "(vizualizare completă)",
      moreArticles: "Mai multe articole",
      loading: "Se încarcă…",
      errorGeneric: "Comparațiile nu pot fi încărcate momentan.",
    },
    dates: {
      unspecified: "Nespecificată",
    },
  },
  en: {
    seo: {
      title: "Daily Legislative Changes from the Official Gazette & Law Alerts | Legea Aplicată",
      description: "Official daily monitoring of amendments across 40 fundamental normative acts (Civil, Criminal, Tax, Labor Codes, etc.). Full verbatim amended text, effective dates, and automated email alerts.",
      keywords: "legislative changes, official gazette romania, law alerts, romanian amended civil code, romanian amended criminal code, romanian legal updates, daily legislation",
      ogTitle: "Daily Legislative Changes & Official Gazette Alerts — Legea Aplicată",
      ogDescription: "All amendments, supplements, and repeals across Romania's major codes. Automated daily email alerts for legal practitioners and professionals.",
      ogImageAlt: "Legea Aplicată — Real-time Legislative Amendments Tracking from the Official Gazette",
      legislationName: "Monitored Legislative Amendments — 40 Fundamental Acts",
      legislationDesc: "Continuous monitoring system for Romanian legislative amendments, additions, and repeals.",
    },
    hero: {
      titlePre: "Daily Legislative Changes from the ",
      titleHighlight: "Official Gazette",
      subtitle: "Track real-time amendments, supplements, and repeals across Romania's 40 fundamental codes and laws. Receive automated email alerts at 12:00 on the day news is published.",
      alertCtaTitle: "Want email alerts when a law in your field changes?",
      alertCtaDesc: "Select your desired fields (Criminal, Civil, Tax, Labor, Administrative, etc.) and receive by email (Pro feature) the official summary and full verbatim text of amendments.",
      alertCtaButton: "Activate Email Alerts (Pro)",
    },
    categories: {
      ariaLabel: "Act category",
      all: "All categories",
      legislation: "Legislative amendments",
      ril: "RIL — High Court (ICCJ)",
      hg: "Government Decisions",
    },
    search: {
      placeholder: "Search by act (e.g., Fiscal Code, GEO 32, Law 57, criminal)...",
      clear: "Clear",
      counter: "Showing: {filtered} of {total} recent amendments",
    },
    domains: {
      all: "All legal fields (40 acts)",
      penal: "⚖️ Criminal Law & Procedure",
      civil: "📜 Civil Law & Procedure",
      fiscal: "💼 Tax Law & Tax Procedure",
      munca: "👥 Labor Law & Social Security",
      administrativ: "🏛️ Administrative Law",
      comercial: "🏢 Companies & Commercial Law",
    },
    archive: {
      title: "Permanent Archive of Legislative Amendments",
      subtitle: "From newest to oldest, by publication date. Imported amendments remain permanently available in the archive.",
      error: "The archive cannot be loaded at this moment.",
      retry: "Retry",
      loading: "Loading legislative updates...",
      emptyTitle: "No amendments found",
      emptyDesc: "Try changing your search keywords or select 'All legal fields'.",
      resetFilters: "Reset filters",
      loadMore: "Load older amendments ({count} remaining)",
    },
    card: {
      publishedMo: "Published in Off. Gaz.:",
      effectiveDate: "Entry into force:",
      articles: "Articles:",
      moreArticles: "+{count} more",
      detailsLink: "Amendment details →",
      consolidatedText: "Consolidated text",
      notifyMe: "Notify me",
      consolidatedTitle: "Consult consolidated articles in Legea Aplicată",
      notifyTitle: "Configure email notifications for this act",
      openConsolidatedArticle: "Open consolidated Article {art} in Legea Aplicată",
      relatedArticleTitle: "Related article AI-classified in {ref}",
    },
    types: {
      completare: "SUPPLEMENT",
      modificare: "AMENDMENT",
      abrogare: "REPEAL",
      suspendare: "SUSPENSION",
      prorogare: "EXTENSION",
      derogare: "DEROGATION",
      interpretare: "INTERPRETATION",
    },
    specialSection: {
      ariaLabel: "RIL and special API normative interventions",
      tablistAria: "Types of enactments and normative interventions",
      badge: "Mandatory Case Law & Related Enactments",
      title: "High Court Rulings (RIL / HP), Government Decisions & Legislative Derogations",
      description: "Beyond direct code article amendments, find mandatory High Court of Cassation and Justice rulings (Appeals in the Interest of Law — RIL and Preliminary Rulings — HP), Government Decisions (HG) containing application rules, Emergency Ordinances (GEO / GO), and derogations, suspensions, and postponed deadlines affecting article enforcement.",
      tabRil: "⚖️ High Court Rulings (RIL & HP)",
      tabHg: "🏛️ Government Decisions & Norms (HG)",
      tabEffects: "🔗 Derogations, Suspensions & Extensions",
      tabOug: "⚡ Emergency Ordinances & Ordinances (GEO / GO)",
      badgeRil: "RIL / High Court Ruling",
      badgeHg: "Government Decision (HG)",
      badgeOug: "GEO / GO",
      badgeEffects: "Derogation / Application Norm",
      published: "Published:",
      targetArticles: "Target articles:",
      viewAnalysis: "View analysis & official text →",
      consolidatedCode: "Consolidated code",
    },
    howItWorks: {
      title: "How the legislative tracking system works on LegeaAplicată",
      step1Title: "1. Daily Official Crawl at 05:20",
      step1Desc: "Every morning at 05:20 (Europe/Bucharest), our infrastructure automatically synchronizes official changes to capture new normative relationships affecting Romania's 40 fundamental laws.",
      step2Title: "2. Verbatim Text & Reconciliation",
      step2Desc: "We don't merely notify you about a law's title: we extract the complete verbatim amended text, affected articles, and an official summary prepared for immediate legal clarity.",
      step3Title: "3. Email Alerts at 12:00",
      step3Desc: "Pro subscribers who enabled email notifications receive an HTML-styled legislative bulletin daily at 12:00 (on days with updates), filtered strictly to their chosen legal fields starting from subscription date.",
    },
    faq: {
      title: "Frequently Asked Questions about Legislative Amendments",
      bottomTextPre: "Discover all 45 legal tools in the ",
      bottomGuide: "Features Guide (45 tools)",
      bottomTextMid: " or consult your ",
      bottomCases: "Followed Cases",
      bottomTextPost: ".",
      q1: "How are legislative amendments monitored and ingested?",
      a1: "The platform runs an automated job daily at 05:20 (Europe/Bucharest) synchronizing changes directly from official sources. All amendments, supplements, and repeals across the 40 fundamental codes and laws are reconciled and ingested verbatim.",
      q2: "What time are email alerts sent and what do they contain?",
      a2: "The server sends alerts to subscribed users at 12:00 on days when updates appear in selected fields. The email includes the amended act, modifying act, Official Gazette number, effective date, affected articles, official summary, and the full verbatim amended text.",
      q3: "Retroactive alerts: do I receive past amendments?",
      a3: "No. Under the strict subscription rule ('effective from check date'), a user exclusively receives changes published after the exact moment they checked the field and enabled email alerts.",
      q4: "How can I disable email alerts?",
      a4: "Unsubscribing is instantaneous. You can uncheck the email option at any time in the Followed Cases page (Legislative Changes tab) or in My Account under Settings & Legislative Alerts.",
      q5: "Which 40 normative acts are monitored?",
      a5: "Monitoring covers Romania's major codes (Civil Code, Criminal Code, Civil Procedure Code, Criminal Procedure Code, Fiscal Code, Tax Procedure Code, Labor Code, Administrative Code, Forestry Code, Aviation Code, etc.) and core statutory laws: Companies Law 31/1990, Insolvency Law 85/2014, Land Registry Law 7/1996, Social Dialogue Law 367/2022, Administrative Litigation Law 554/2004, etc.",
    },
    detail: {
      ariaLabel: "Selected amendment",
      backLink: "← Back to all amendments",
      errorNotFound: "Amendment was not found in the archive.",
      errorGeneric: "Details cannot be loaded at this moment.",
      retry: "Retry",
      loading: "Loading amendment…",
      effectiveDate: "Entry into force:",
      consolidatedTitle: "Amended articles in consolidated format",
      consolidatedSubtitle: "Consult the official consolidated text directly in Legea Aplicată, without external redirects.",
      openConsolidatedSingle: "Open consolidated Art. {art}",
      openConsolidatedAll: "Open all {count} consolidated articles",
      openSpecificArticle: "Or open a specific article:",
      consolidatedNoticeTitle: "Consolidated format in Legea Aplicată",
      consolidatedNoticeSubtitle: "The official source did not specify individual articles in metadata (general intervention or derogation).",
      openTarget: "Open {target}",
      verbatimHeader: "Legislative amendments content (official text)",
      noModifiedText: "The official source does not provide separate modified text for this record. Consult the verified consolidated version above.",
      viewArticleConsolidated: "View Art. {art} consolidated →",
      viewArticleInContext: "View Art. {art} in consolidated context →",
      viewLinkConsolidated: "View {num}{ref} consolidated →",
      viewLinkInContext: "View {num}{ref} in consolidated context →",
      fullActSummary: "Full official act text",
    },
    consolidation: {
      noticeAria: "Legislative text update",
      notice: "Consolidated format from {date}, verified in-app on {verifiedAt}.",
      pending: "A new automated check is in progress. The text above retains the last confirmed version.",
      fullLink: "Consult full consolidated text in the app →",
      prevVersion: "Previous version in app",
      verifiedText: "Verified consolidated text",
      newArticle: "New article in local database.",
      initialRecon: "Initial retrieval: comparison with version previously stored in the app. May include multiple legislative amendments.",
    },
    consolidationChanges: {
      title: "Consolidated forms comparison · {count} articles",
      subtitle: "Differences from the last verified local version. May include effects of multiple modifying acts and are not exclusively attributable to the selected API record.",
      openArticle: "Open consolidated article →",
      fullView: "(full view)",
      moreArticles: "More articles",
      loading: "Loading…",
      errorGeneric: "Comparisons cannot be loaded at this moment.",
    },
    dates: {
      unspecified: "Unspecified",
    },
  },
  fr: {
    seo: {
      title: "Modifications Législatives au Quotidien du Journal Officiel & Alertes Lois | Legea Aplicată",
      description: "Suivi officiel quotidien des modifications des 40 actes normatifs fondamentaux (Codes Civil, Pénal, Fiscal, Travail, etc.). Texte intégral verbatim, entrées en vigueur et alertes e-mail.",
      keywords: "modifications legislatives roumanie, journal officiel roumanie, alertes lois roumanie, code civil roumain modifie, code penal roumain modifie, actualites juridiques roumanie",
      ogTitle: "Modifications Législatives au Quotidien & Alertes Journal Officiel — Legea Aplicată",
      ogDescription: "Toutes les modifications, ajouts et abrogations des grands codes roumains. Alertes automatiques quotidiennes par e-mail pour juristes et professionnels.",
      ogImageAlt: "Legea Aplicată — Veille Quotidienne des Modifications Législatives du Journal Officiel",
      legislationName: "Modifications Législatives Surveillées — 40 Actes Fondamentaux",
      legislationDesc: "Système continu de suivi des modifications, ajouts et abrogations législatives en Roumanie.",
    },
    hero: {
      titlePre: "Modifications Législatives au Quotidien du ",
      titleHighlight: "Journal Officiel",
      subtitle: "Suivez en temps réel les modifications, ajouts et abrogations des 40 codes et lois fondamentales de Roumanie. Recevez des alertes automatiques par e-mail à 12h00 le jour de publication des nouveautés.",
      alertCtaTitle: "Souhaitez-vous des alertes par e-mail lorsqu'une loi de votre matière est modifiée ?",
      alertCtaDesc: "Sélectionnez les matières souhaitées (Pénal, Civil, Fiscal, Travail, Administratif, etc.) et recevez par e-mail (fonction Pro) la synthèse et le texte intégral verbatim des modifications.",
      alertCtaButton: "Activer les Alertes Email (Pro)",
    },
    categories: {
      ariaLabel: "Catégorie de l'acte",
      all: "Toutes les catégories",
      legislation: "Modifications législatives",
      ril: "RIL — Haute Cour (ICCJ)",
      hg: "Arrêtés du Gouvernement",
    },
    search: {
      placeholder: "Rechercher par acte (ex. : Code Fiscal, OUG 32, Loi 57, pénal)...",
      clear: "Effacer",
      counter: "Affichées : {filtered} sur {total} modifications récentes",
    },
    domains: {
      all: "Toutes les matières (40 actes)",
      penal: "⚖️ Droit Pénal & Procédure",
      civil: "📜 Droit Civil & Procédure",
      fiscal: "💼 Droit Fiscal & Procédure",
      munca: "👥 Droit du Travail & Sécurité",
      administrativ: "🏛️ Droit Administratif",
      comercial: "🏢 Sociétés & Droit Commercial",
    },
    archive: {
      title: "Archive Permanente des Modifications Législatives",
      subtitle: "Du plus récent au plus ancien, selon la date de publication. Les modifications importées restent disponibles en permanence dans l'archive.",
      error: "L'archive ne peut pas être chargée pour le moment.",
      retry: "Réessayer",
      loading: "Chargement des nouveautés législatives...",
      emptyTitle: "Aucune modification trouvée",
      emptyDesc: "Essayez de modifier les termes de recherche ou sélectionnez « Toutes les matières ».",
      resetFilters: "Réinitialiser les filtres",
      loadMore: "Charger les modifications plus anciennes ({count} restantes)",
    },
    card: {
      publishedMo: "Publié au J.O. :",
      effectiveDate: "Entrée en vigueur :",
      articles: "Articles :",
      moreArticles: "+{count} autres",
      detailsLink: "Détails de la modification →",
      consolidatedText: "Texte consolidé",
      notifyMe: "Me notifier",
      consolidatedTitle: "Consulter les articles consolidés dans Legea Aplicată",
      notifyTitle: "Configurer les alertes e-mail pour cet acte",
      openConsolidatedArticle: "Ouvrir l'Article {art} consolidé dans Legea Aplicată",
      relatedArticleTitle: "Article connexe classifié par IA dans {ref}",
    },
    types: {
      completare: "COMPLÉMENT",
      modificare: "MODIFICATION",
      abrogare: "ABROGATION",
      suspendare: "SUSPENSION",
      prorogare: "PROROGATION",
      derogare: "DÉROGATION",
      interpretare: "INTERPRÉTATION",
    },
    specialSection: {
      ariaLabel: "RIL et interventions normatives spéciales API",
      tablistAria: "Types d'actes et interventions normatives",
      badge: "Jurisprudence Obligatoire & Actes Connexes",
      title: "Décisions Haute Cour (RIL / HP), Arrêtés du Gouvernement & Dérogations Législatives",
      description: "Au-delà des modifications directes des articles de codes, retrouvez ici les arrêts obligatoires de la Haute Cour de Cassation et de Justice (Pourvois dans l'Intérêt de la Loi — RIL et Décisions Préalables — HP), les Arrêtés du Gouvernement (HG) portant normes d'application, les Ordonnances d'Urgence (OUG / OG), ainsi que les dérogations, suspensions et prorogations.",
      tabRil: "⚖️ Décisions Haute Cour (RIL & HP)",
      tabHg: "🏛️ Arrêtés du Gouvernement & Normes (HG)",
      tabEffects: "🔗 Dérogations, Suspensions & Prorogations",
      tabOug: "⚡ Ordonnances d'Urgence & Ordonnances (OUG / OG)",
      badgeRil: "RIL / Arrêt Haute Cour",
      badgeHg: "Arrêté du Gouvernement (HG)",
      badgeOug: "OUG / OG",
      badgeEffects: "Dérogation / Norme d'Application",
      published: "Publié :",
      targetArticles: "Articles visés :",
      viewAnalysis: "Voir l'analyse & texte officiel →",
      consolidatedCode: "Code consolidé",
    },
    howItWorks: {
      title: "Comment fonctionne le système de veille législative sur LegeaAplicată",
      step1Title: "1. Crawl Officiel Quotidien à 05h20",
      step1Desc: "Chaque matin à 05h20 (Europe/Bucarest), notre infrastructure synchronise automatiquement les modifications officielles pour recueillir les nouvelles relations normatives affectant les 40 lois fondamentales.",
      step2Title: "2. Texte Verbatim & Réconciliation",
      step2Desc: "Nous ne fournissons pas une simple notification du titre de la loi : nous extrayons le texte intégral verbatim modifié, les articles concernés et un résumé officiel rédigé pour une clarté juridique immédiate.",
      step3Title: "3. Alertes Email à 12h00",
      step3Desc: "Les abonnés Pro ayant activé les alertes reçoivent chaque jour à 12h00 (les jours avec nouveautés) le bulletin législatif complet au format HTML, strictement limité à leurs matières d'intérêt dès la date d'inscription.",
    },
    faq: {
      title: "Foire Aux Questions sur les Modifications Législatives",
      bottomTextPre: "Découvrez l'ensemble des 45 outils juridiques dans le ",
      bottomGuide: "Guide des Fonctions (45 outils)",
      bottomTextMid: " ou consultez vos ",
      bottomCases: "Dossiers Suivis",
      bottomTextPost: ".",
      q1: "Comment les modifications législatives sont-elles surveillées et importées ?",
      a1: "La plateforme exécute un processus automatisé chaque jour à 05h20 (Europe/Bucarest) qui synchronise les modifications directement depuis la législation officielle. Toutes les modifications, ajouts et abrogations des 40 actes normatifs fondamentaux sont réconciliés et intégrés verbatim.",
      q2: "À quelle heure les alertes e-mail sont-elles envoyées et que contiennent-elles ?",
      a2: "Le serveur envoie les alertes aux utilisateurs abonnés à 12h00 le jour où des nouveautés paraissent dans leurs matières sélectionnées. L'e-mail comprend l'acte modifié, l'acte modificateur, le numéro du Journal Officiel, la date d'entrée en vigueur, les articles concernés, le résumé officiel et le texte intégral verbatim modifié.",
      q3: "Alertes rétroactives : reçois-je les modifications passées ?",
      a3: "Non. Conformément à la règle stricte de souscription (« à compter de la date d'activation »), un utilisateur reçoit exclusivement les modifications publiées après le moment exact où il a coché la matière d'intérêt et validé la réception des alertes par e-mail.",
      q4: "Comment puis-je désactiver les alertes par e-mail ?",
      a4: "La désactivation est instantanée. Vous pouvez décocher l'option e-mail à tout moment depuis la page Dossiers Suivis (onglet Modifications Législatives d'Intérêt) ou depuis Mon Compte dans la rubrique Paramètres & Alertes Législatives.",
      q5: "Quels sont les 40 actes normatifs surveillés ?",
      a5: "La surveillance couvre les grands codes de Roumanie (Code Civil, Code Pénal, Code de Procédure Civile, Code de Procédure Pénale, Code Fiscal, Code de Procédure Fiscale, Code du Travail, Code Administratif, Code Forestier, Code Aérien, etc.) ainsi que les lois organiques fondamentales : Loi sur les sociétés 31/1990, Loi sur les faillites 85/2014, Loi sur le cadastre 7/1996, Loi sur le dialogue social 367/2022, Loi sur le contentieux administratif 554/2004, etc.",
    },
    detail: {
      ariaLabel: "Modification sélectionnée",
      backLink: "← Retour à toutes les modifications",
      errorNotFound: "La modification n'a pas été trouvée dans l'archive.",
      errorGeneric: "Les détails ne peuvent pas être chargés pour le moment.",
      retry: "Réessayer",
      loading: "Chargement de la modification…",
      effectiveDate: "Entrée en vigueur :",
      consolidatedTitle: "Articles modifiés sous forme consolidée",
      consolidatedSubtitle: "Consultez le texte consolidé officiel directement dans Legea Aplicată, sans redirections externes.",
      openConsolidatedSingle: "Ouvrir l'Art. {art} consolidé",
      openConsolidatedAll: "Ouvrir l'ensemble des {count} articles consolidés",
      openSpecificArticle: "Ou ouvrir un article spécifique :",
      consolidatedNoticeTitle: "Forme consolidée dans Legea Aplicată",
      consolidatedNoticeSubtitle: "La source officielle n'a pas indiqué d'articles précis dans les métadonnées (intervention générale ou dérogation).",
      openTarget: "Ouvrir {target}",
      verbatimHeader: "Contenu des modifications législatives (texte officiel)",
      noModifiedText: "La source officielle ne fournit pas de texte modifié séparé pour cet enregistrement. Consultez la forme consolidée vérifiée ci-dessus.",
      viewArticleConsolidated: "Voir l'Art. {art} consolidé →",
      viewArticleInContext: "Voir l'Art. {art} en contexte consolidé →",
      viewLinkConsolidated: "Voir {num}{ref} consolidé →",
      viewLinkInContext: "Voir {num}{ref} en contexte consolidé →",
      fullActSummary: "Texte intégral de l'acte officiel",
    },
    consolidation: {
      noticeAria: "Mise à jour du texte législatif",
      notice: "Forme consolidée du {date}, vérifiée dans l'application le {verifiedAt}.",
      pending: "Une nouvelle vérification automatique est en cours. Le texte ci-dessus conserve la dernière version confirmée.",
      fullLink: "Consulter la forme consolidée intégrale dans l'application →",
      prevVersion: "Version précédente dans l'application",
      verifiedText: "Texte consolidé vérifié",
      newArticle: "Nouvel article dans la base locale.",
      initialRecon: "Récupération initiale : comparaison avec la version précédemment conservée dans l'application. Peut inclure plusieurs modifications législatives.",
    },
    consolidationChanges: {
      title: "Comparaison des versions consolidées · {count} articles",
      subtitle: "Différences par rapport à la dernière version locale vérifiée. Peuvent inclure les effets de plusieurs actes modificateurs et ne sont pas exclusivement attribuables à l'enregistrement API sélectionné.",
      openArticle: "Ouvrir l'article consolidé →",
      fullView: "(vue complète)",
      moreArticles: "Plus d'articles",
      loading: "Chargement…",
      errorGeneric: "Les comparaisons ne peuvent pas être chargées pour le moment.",
    },
    dates: {
      unspecified: "Non précisée",
    },
  },
};

export default legislativeChangesTranslations;
